<script setup>
import { ref, inject, provide, shallowRef, getCurrentInstance } from "vue";
import KeywordStep from "./Partials/Requests/KeywordStep.vue";
import CustomizeStep from "./Partials/Requests/CustomizeStep.vue";
import PlanningStep from "./Partials/PlanningStep.vue";
import FinishStep from "./Partials/FinishStep.vue";
import ContentStep from "./Partials/ContentStep.vue";
import TitleAndLanguage from "../Shared/TitleAndLanguage.vue";
import Stepper from "@Components/Stepper/Stepper.vue";
import { getType } from "@Utils/project";
import { countRequests } from "@Utils/requests";
import { useForm } from "@/form";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import Breadcrumb from "@Components/Breadcrumb.vue";
import _find from "lodash/find";
import { format } from "date-fns";

const $notify = inject("$notify");
const { t: __ } = useI18n();
const createdProject = ref();
const stepper = ref();

const router = useRouter();
const route = useRoute();

// request settings
const requestsSettings = ref({});
provide("requestsSettings", requestsSettings);

const { params: routeParams } = useRoute();

const [personas, users, templates, statuses, categories, postTypes] = await Promise.all([
    new Promise((resolve, reject) => {
        useForm()
            .post("personas_index")
            .then((response) => {
                resolve(response);
            })
            .catch((response) => {
                reject();
            });
    }),
    new Promise((resolve, reject) => {
        useForm()
            .post("wordpress_users")
            .then((response) => {
                resolve(response);
            })
            .catch((response) => {
                reject();
            });
    }),
    new Promise((resolve, reject) => {
        useForm()
            .post("wordpress_templates")
            .then((response) => {
                resolve(response);
            })
            .catch((response) => {
                reject();
            });
    }),
    new Promise((resolve, reject) => {
        useForm()
            .post("wordpress_statuses")
            .then((response) => {
                resolve(response);
            })
            .catch((response) => {
                reject();
            });
    }),
    new Promise((resolve, reject) => {
        useForm()
            .post("wordpress_categories")
            .then((response) => {
                resolve(response);
            })
            .catch((response) => {
                reject();
            });
    }),
    new Promise((resolve, reject) => {
        useForm()
            .post("wordpress_post_types")
            .then((response) => {
                resolve(response);
            })
            .catch((response) => {
                reject();
            });
    }),
]);

let type = "bulk";
if (routeParams.type) {
    if (getType(route.params.type)) {
        type = getType(route.params.type).key;
    }
}

let postType = "post";
if (routeParams.postType) {
    if (_find(postTypes, ["name", routeParams.postType]) !== undefined) {
        postType = routeParams.postType;
    }
}

let style = undefined;
if (routeParams.style) {
    style = routeParams.style;
}

const form = useForm({
    language: getCurrentInstance().appContext.config.globalProperties.$app.default_locale,
    persona_id: null,
    type: type,
    name: "Nouveau projet",
    requests: [],
    modules: {
        models: {
            text: "gpt-4.1-mini",
            image: "flux_1_dev",
        },
        content: {
            writing_style: null,
            sources: {
                enabled: false,
                no_follow: false,
                blank: false,
            },
            custom_instructions: {
                profile: null,
                images: null,
                text: null,
            },
            blocks: [],
            automatic: {
                enabled: true,
                text_length: "medium",
                images: {
                    enabled: true,
                    max_images: 3,
                },
                youtube: {
                    enabled: true,
                },
                embeds: {
                    enabled: true,
                },
                internal_links: {
                    enabled: true,
                },
                external_links: {
                    enabled: true,
                    custom_links: [],
                },
                custom_links: {
                    enabled: false,
                    links: [],
                },
                lists: {
                    enabled: true,
                },
                tables: {
                    enabled: true,
                },
                bold_words: {
                    enabled: true,
                },
                emojis: {
                    enabled: false,
                },
                summary: {
                    enabled: true,
                },
                brief: {
                    enabled: true,
                },
                faq: {
                    enabled: true,
                },
                toolbox: {
                    enabled: true,
                },
                keywords: {
                    automatic: {
                        enabled: true,
                    },
                    custom: [],
                },
                amazon: {
                    enabled: false,
                    template: "grid_3x",
                    max_products: 6,
                },
            },
        },
        wordpress: {
            thumbnail: {
                enabled: true,
            },
            post_type: postType,
            author_id: null,
            parent_page_id: null,
            categories: {
                automatic: {
                    enabled: false,
                },
                custom: [],
            },
            tags: {
                automatic: {
                    enabled: false,
                },
                custom: [],
            },
            template: null,
            status: "publish",
            rewrite_permalink: {
                enabled: false,
            },
            seo_title: {
                enabled: false,
                emojis: {
                    enabled: false,
                },
            },
            seo_description: {
                enabled: false,
            },
            custom_fields: [],
        },
        autopilot: {
            query: null,
            source_language: getCurrentInstance().appContext.config.globalProperties.$app.default_locale,
            planning: {
                per_day: 2,
                start_time: {
                    hours: 6,
                    minutes: 30,
                },
                end_time: {
                    hours: 21,
                    minutes: 0,
                },
            },
        },
    },
    planning: {
        enabled: true,
        start_date: format(new Date(), "yyyy-MM-dd"),
        days: ["monday", "tuesday", "wednesday", "thursday", "friday"],
        per_period: 1,
        period: "day",
        start_time: {
            hours: 6,
            minutes: 30,
        },
        end_time: {
            hours: 21,
            minutes: 0,
        },
    },
});

const steps = ref([]);

// make requests step
let requestsStep = {
    key: "requests",
    status: "current",
    children: [],
};

// make requests step children
if (type !== "regenerate" && (type !== "bulk" || (type === "bulk" && style !== undefined))) {
    requestsStep.children.push({
        key: "requests-keyword",
        status: "current",
        next: {
            handler: () => {
                if (form.type === "rss" || form.type === "news") {
                    if (requestsSettings.value.now_length !== undefined && requestsSettings.value.now_length > 0) {
                        stepper.value.setCurrentStep(["requests", "customize"]);
                    } else {
                        stepper.value.setCurrentStep("content");
                    }
                } else {
                    stepper.value.setCurrentStep(["requests", "customize"]);
                }
            },
            label: () => {
                if (form.type === "rss" || form.type === "news") {
                    if (requestsSettings.value.now_length !== undefined && requestsSettings.value.now_length > 0) {
                        return __("Générez vos titres");
                    } else {
                        return __("Configurez votre contenu");
                    }
                } else {
                    return __("Générez vos titres");
                }
            },
            disabled: () => {
                if (form.type === "sitemap" || form.type === "url" || form.type === "youtube") {
                    return requestsSettings.value.url === undefined || requestsSettings.value.url.length === 0;
                }

                if (form.type === "news") {
                    return (
                        requestsSettings.value.keyword === undefined ||
                        requestsSettings.value.keyword.length === 0 ||
                        ((requestsSettings.value.now_length === undefined || requestsSettings.value.now_length <= 0) &&
                            (form.modules.autopilot.planning.per_day === undefined || form.modules.autopilot.planning.per_day <= 0))
                    );
                }

                if (form.type === "rss") {
                    return (
                        requestsSettings.value.url === undefined ||
                        requestsSettings.value.url.length === 0 ||
                        ((requestsSettings.value.now_length === undefined || requestsSettings.value.now_length <= 0) &&
                            (form.modules.autopilot.planning.per_day === undefined || form.modules.autopilot.planning.per_day <= 0))
                    );
                }

                return requestsSettings.value.keyword === undefined || requestsSettings.value.keyword.length === 0;
            },
        },
        skip: {
            handler: () => {
                requestsSettings.value.keyword = undefined;
                stepper.value.setCurrentStep(["requests", "customize"]);
            },
            label: () => {
                return __("Ajoutez vos titres manuellement");
            },
            display: false,
        },
        after: async () => {
            try {
                await form.post("project_validate_requests_keyword_step");
                return true;
            } catch {
                return false;
            }
        },
        component: shallowRef(KeywordStep),
    });
}

requestsStep.children.push({
    key: "requests-customize",
    status: "upcoming",
    back: {
        handler: ["requests", "keyword"],
        display: () => {
            return style !== undefined && type !== "regenerate";
        },
    },
    next: {
        handler: "content",
        label: __("Configurez votre contenu"),
        disabled: () => {
            return countRequests(form.requests) === 0;
        },
    },
    after: async () => {
        try {
            await form.post("project_validate_requests_step");
            return true;
        } catch {
            return false;
        }
    },
    component: shallowRef(CustomizeStep),
});

// add request step
steps.value.push(requestsStep);

// content step
steps.value.push({
    key: "content",
    status: "upcoming",
    next: {
        handler: () => {
            if (form.type === "rss" || form.type === "news" || requestsSettings.value.is_siloing) {
                submit();
            } else {
                stepper.value.setCurrentStep("planning");
            }
        },
        label: () => {
            if (form.type === "rss" || form.type === "news" || requestsSettings.value.is_siloing) {
                return __("Lancer ma génération !");
            } else {
                return __("Planifiez vos publications");
            }
        },
    },
    back: {
        handler: () => {
            if (form.type === "rss" || form.type === "news") {
                stepper.value.setCurrentStep(["requests", "keyword"]);
            } else {
                stepper.value.setCurrentStep(["requests", "customize"]);
            }
        },
    },
    after: async () => {
        try {
            await form.post("project_validate_content_text_step");
            return true;
        } catch {
            return false;
        }
    },
    after: async () => {
        try {
            await form.post("project_validate_content_step");
            return true;
        } catch {
            return false;
        }
    },
    component: shallowRef(ContentStep),
    params: {
        personas: personas,
        templates: templates,
        statuses: statuses,
        categories: categories,
        postTypes: postTypes,
        users: users,
    },
});

// planning step
steps.value.push({
    key: "planning",
    status: "upcoming",
    back: {
        handler: "content",
    },
    next: {
        handler: () => {
            submit();
        },
        label: __("Lancer ma génération !"),
    },
    after: async () => {
        try {
            await form.post("project_validate_planning_step");
            return true;
        } catch {
            return false;
        }
    },
    component: shallowRef(PlanningStep),
});

// finish step
steps.value.push({
    key: "finish",
    status: "upcoming",
    next: {
        handler: () => {
            router.push({ name: "publications.index", params: { id: createdProject.value.id } });
        },
        label: __("Aller au projet"),
    },
    component: shallowRef(FinishStep),
    params: {
        project: createdProject,
    },
});

const initialStep = ref(["requests", "keyword"]);
// --------------

const submit = () => {
    if (form.processing) return;

    form.post("projects_store")
        .then((project) => {
            createdProject.value = project;
            stepper.value.setCurrentStep("finish");
        })
        .catch(() => {
            $notify({
                title: "An error occurred while creating project.",
                type: "error",
            });
        });
};

if (type !== "regenerate" && (type !== "bulk" || (type === "bulk" && style !== undefined))) {
    initialStep.value = ["requests", "keyword"];
} else {
    initialStep.value = ["requests", "customize"];
}
</script>

<template>
    <div>
        <Breadcrumb :pages="[{ name: __('Mes projets'), href: { name: 'projects.index' } }, { name: __(getType(form.type)?.label || '') }]" />
        <Stepper ref="stepper" :steps="steps" v-model="form" :initial="initialStep">
            <template #before>
                <TitleAndLanguage v-model="form" />
            </template>
        </Stepper>
    </div>
</template>
